import { useState } from 'react';
import type { FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAdmin } from '../../context/admin';
import { NAME_MAX } from '../../lib/access';
import type { AdminWell, Company } from '../../lib/access';
import { ADMIN_SIMULATED, MAX_DATA_LEVEL, WELL_STATUSES, deleteWell, saveWell } from '../../lib/api/admin';
import type { WellInput } from '../../lib/api/admin';
import { ActionMessages, AdminShell } from './AdminShell';
import { plural, usePaging, useView } from './listing';
import { DeleteLink, Dialog, DialogButtons, DialogError, ListFoot, ViewToggle } from './parts';
import { useAdminAction } from './useAdminAction';
import { SkeletonTable } from '../../components/ui/Skeleton';

type Draft = Omit<WellInput, 'latitude' | 'longitude'> & { latitude: string; longitude: string };

const EMPTY: Draft = {
  name: '',
  field: '',
  country: '',
  rig: '',
  purpose: '',
  status: WELL_STATUSES[0],
  latitude: '',
  longitude: '',
  companyId: null,
  sources: [],
};

function draftOf(well: AdminWell): Draft {
  return {
    name: well.name,
    field: well.field ?? '',
    country: well.country ?? '',
    rig: well.rig ?? '',
    purpose: well.purpose ?? '',
    status: well.status || WELL_STATUSES[0],
    latitude: well.latitude == null ? '' : String(well.latitude),
    longitude: well.longitude == null ? '' : String(well.longitude),
    companyId: well.companyId,
    sources: well.sources.map((s) => ({ ...s })),
  };
}

/** "" is "not given"; anything else has to be a number, or the save is refused. */
function degrees(value: string): number | null {
  if (value.trim() === '') return null;
  const n = Number(value);
  return Number.isFinite(n) ? n : Number.NaN;
}

function move<T>(list: T[], index: number, by: -1 | 1): T[] {
  const to = index + by;
  if (to < 0 || to >= list.length) return list;
  const next = [...list];
  [next[index], next[to]] = [next[to], next[index]];
  return next;
}

/**
 * The wells, and which company each belongs to. A well the API already serves
 * keeps the API's details; a new one is created here.
 *
 * Every well belongs to one company, and people are put on wells of their own
 * company only. Each well also lists who supplies its data: the same well is
 * measured by several service companies, and the order says how far each is
 * trusted.
 */
export function Wells() {
  const { store, allWells, wellsLoaded, wellsError } = useAdmin();
  const { busy, error, notice, run, clear } = useAdminAction();
  const navigate = useNavigate();
  const [view, setView] = useView();

  /** null: closed. 0: a new well. Otherwise the id of the well being edited. */
  const [editing, setEditing] = useState<number | null>(null);
  const [search, setSearch] = useState('');
  const [company, setCompany] = useState<'all' | 'none' | number>('all');
  const [status, setStatus] = useState('all');

  const companyName = (id: number | null) => store.companies.find((c) => c.id === id)?.name;

  const filtered = allWells.filter((w) => {
    const q = search.trim().toLowerCase();
    if (q && !`${w.name} ${w.field ?? ''} ${w.rig ?? ''} ${w.country ?? ''}`.toLowerCase().includes(q)) return false;
    if (company === 'none' ? w.companyId !== null : company !== 'all' && w.companyId !== company) return false;
    if (status !== 'all' && w.status !== status) return false;
    return true;
  });
  const paging = usePaging(filtered);
  const filter = (apply: () => void) => {
    apply();
    paging.first();
  };

  const current = editing ? allWells.find((w) => w.id === editing) : undefined;
  const unfiled = allWells.filter((w) => w.companyId === null).length;
  const noCompanies = store.companies.length === 0;

  function open(well: AdminWell | null) {
    clear();
    setEditing(well?.id ?? 0);
  }

  async function onSave(draft: Draft) {
    const id = editing || null;
    const input: WellInput = { ...draft, latitude: degrees(draft.latitude), longitude: degrees(draft.longitude) };
    const ok = await run(
      () =>
        saveWell(id, input, {
          local: current?.local ?? true,
          names: allWells.filter((w) => w.id !== id).map((w) => w.name),
        }),
      id ? `${draft.name.trim()} saved.` : `${draft.name.trim()} created.`,
    );
    if (ok) setEditing(null);
  }

  async function onDelete(well: AdminWell) {
    const ok = await run(() => deleteWell(well.id), `${well.name} deleted.`);
    if (ok) setEditing(null);
  }

  const sources = (well: AdminWell) =>
    well.sources.length === 0 ? (
      <span className="cmmuted">—</span>
    ) : (
      well.sources.map((s) => (
        <span key={s.id} className="cmchip">
          {s.company} · L{s.level}
          {s.realtime && ' · RT'}
        </span>
      ))
    );

  const keptIn = (well: AdminWell) => (
    <span className={well.local ? 'cmtag cmtag--plain' : 'cmtag'}>{well.local ? 'This browser' : 'API'}</span>
  );

  return (
    <AdminShell
      active="Wells"
      title="Well Management"
      count={wellsLoaded ? `${allWells.length} ${plural(allWells.length, 'Well')}` : undefined}
      action={
        <button
          type="button"
          className="cmbtn cmbtn--primary"
          onClick={() => open(null)}
          disabled={noCompanies}
          title={noCompanies ? 'Create a company first' : undefined}
        >
          + Add Well
        </button>
      }
    >
      <div className="cmtools">
        <input
          type="search"
          className="cmsearch"
          placeholder="Search well name, field or rig…"
          aria-label="Search well name, field or rig"
          value={search}
          onChange={(e) => filter(() => setSearch(e.target.value))}
        />
        <select
          className="cmselect"
          aria-label="Company"
          value={String(company)}
          onChange={(e) => {
            const v = e.target.value;
            filter(() => setCompany(v === 'all' || v === 'none' ? v : Number(v)));
          }}
        >
          <option value="all">Company: All</option>
          {store.companies.map((c) => (
            <option key={c.id} value={c.id}>
              Company: {c.name}
            </option>
          ))}
          <option value="none">Company: None yet</option>
        </select>
        <select className="cmselect" aria-label="Status" value={status} onChange={(e) => filter(() => setStatus(e.target.value))}>
          <option value="all">Status: All</option>
          {WELL_STATUSES.map((s) => (
            <option key={s} value={s}>
              Status: {s}
            </option>
          ))}
        </select>

        <span className="cmspacer" />
        <ViewToggle view={view} onChange={setView} />
      </div>

      {wellsError && (
        <div className="alert cmmsg" role="alert">
          {wellsError} Only wells created here are listed.
        </div>
      )}
      <ActionMessages error={error} notice={notice} inDialog={editing !== null} />

      {noCompanies && (
        <p className="cmempty">
          A well belongs to a company. Create the company first, on the <Link to="/web/Admin/Companies">Companies</Link> tab.
        </p>
      )}

      {!wellsLoaded && <SkeletonTable rows={6} cols={5} label="Loading wells" />}
      {wellsLoaded && allWells.length === 0 && !noCompanies && <p className="cmempty">No wells yet.</p>}
      {wellsLoaded && allWells.length > 0 && filtered.length === 0 && <p className="cmempty">No well matches that search.</p>}

      {paging.shown.length > 0 && view === 'list' && (
        <div className="cmtable-wrap">
          <table className="cmtable">
            <thead>
              <tr>
                <th>Well name</th>
                <th>Company</th>
                <th>Country</th>
                <th>Rig</th>
                <th>Status</th>
                <th title="The service companies that supply this well's data, most trusted first">Data sources</th>
                {ADMIN_SIMULATED && <th>Kept in</th>}
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {paging.shown.map((well) => (
                <tr key={well.id}>
                  <td>
                    <div className="cmname">
                      <span>
                        <button type="button" className="cmlink" onClick={() => open(well)}>
                          {well.name}
                        </button>
                        {well.field && <small>{well.field}</small>}
                      </span>
                    </div>
                  </td>
                  <td>{companyName(well.companyId) ?? <span className="cmnone">No company yet</span>}</td>
                  <td>{well.country || '—'}</td>
                  <td>{well.rig || '—'}</td>
                  <td>{well.status || '—'}</td>
                  <td className="cmwrap">{sources(well)}</td>
                  {ADMIN_SIMULATED && <td>{keptIn(well)}</td>}
                  <td>
                    <span className="cmactions">
                      <button type="button" className="cmbtn cmbtn--small" onClick={() => open(well)}>
                        Edit
                      </button>
                      <button type="button" className="cmbtn cmbtn--small" onClick={() => navigate(`/web?wellId=${well.id}`)}>
                        Open
                      </button>
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {paging.shown.length > 0 && view === 'grid' && (
        <div className="cmgrid">
          {paging.shown.map((well) => (
            <article key={well.id} className="cmcard">
              <header>
                <span>
                  <button type="button" className="cmlink" onClick={() => open(well)}>
                    {well.name}
                  </button>
                  <small>{[well.field, well.country].filter(Boolean).join(' · ') || '—'}</small>
                </span>
                {ADMIN_SIMULATED && keptIn(well)}
              </header>
              <dl>
                <div>
                  <dt>Company</dt>
                  <dd>{companyName(well.companyId) ?? <span className="cmnone">No company yet</span>}</dd>
                </div>
                <div>
                  <dt>Rig</dt>
                  <dd>{well.rig || '—'}</dd>
                </div>
                <div>
                  <dt>Status</dt>
                  <dd>{well.status || '—'}</dd>
                </div>
                <div>
                  <dt>Sources</dt>
                  <dd className="cmchips">{sources(well)}</dd>
                </div>
              </dl>
              <footer className="cmactions">
                <button type="button" className="cmbtn cmbtn--small" onClick={() => open(well)}>
                  Edit
                </button>
                <button type="button" className="cmbtn cmbtn--small" onClick={() => navigate(`/web?wellId=${well.id}`)}>
                  Open
                </button>
              </footer>
            </article>
          ))}
        </div>
      )}

      <ListFoot paging={paging} one="well" />

      {wellsLoaded && unfiled > 0 && company !== 'none' && (
        <p className="cmunfiled">
          {unfiled} {plural(unfiled, 'well is', 'wells are')} not filed under a company yet.{' '}
          <button type="button" className="adminlink" onClick={() => filter(() => setCompany('none'))}>
            Show {plural(unfiled, 'it', 'them')}
          </button>
        </p>
      )}

      {editing !== null && (
        <WellDialog
          key={editing}
          well={current ?? null}
          companies={store.companies}
          startCompany={typeof company === 'number' ? company : (store.companies[0]?.id ?? null)}
          busy={busy}
          error={error}
          onClose={() => setEditing(null)}
          onSave={(draft) => void onSave(draft)}
          // With a real API any well may be deleted; the server refuses one
          // with anything on it, and says what.
          onDelete={current && (current.local || !ADMIN_SIMULATED) ? () => void onDelete(current) : undefined}
        />
      )}
    </AdminShell>
  );
}

function WellDialog({
  well,
  companies,
  startCompany,
  busy,
  error,
  onClose,
  onSave,
  onDelete,
}: {
  well: AdminWell | null;
  companies: Company[];
  startCompany: number | null;
  busy: boolean;
  error: string | null;
  onClose: () => void;
  onSave: (draft: Draft) => void;
  onDelete?: () => void;
}) {
  const [draft, setDraft] = useState<Draft>(() => (well ? draftOf(well) : { ...EMPTY, companyId: startCompany }));

  // In the simulation a well the mock serves keeps the mock's details: only
  // its company and sources are set here. With a real API the server keeps
  // every well, and every detail of one is the superadmin's to change.
  const fromApi = ADMIN_SIMULATED && well !== null && !well.local;

  const patch = (change: Partial<Draft>) => setDraft((d) => ({ ...d, ...change }));
  const patchSources = (change: (sources: Draft['sources']) => Draft['sources']) =>
    setDraft((d) => ({ ...d, sources: change(d.sources) }));

  function submit(e: FormEvent) {
    e.preventDefault();
    onSave(draft);
  }

  return (
    <Dialog
      title={well ? `Edit Well — ${well.name}` : 'New Well'}
      subtitle={fromApi ? 'Served by the API · company and data sources are set here' : undefined}
      size="wide"
      busy={busy}
      onClose={onClose}
      onSubmit={submit}
      footer={<DialogButtons busy={busy} label={well ? 'Save Changes' : 'Create Well'} onClose={onClose} />}
    >
      <DialogError error={error} />

      {fromApi && (
        <p className="cminfo">
          This well comes from the API, so its name and details are changed there. Its company and data sources are
          set here.
        </p>
      )}

      <div className="cmfields">
        <label className="cmfield">
          <span>Well Name *</span>
          <input
            type="text"
            value={draft.name}
            maxLength={NAME_MAX}
            required
            readOnly={fromApi}
            autoFocus={!fromApi}
            placeholder="e.g. Tapis Deep-1"
            onChange={(e) => patch({ name: e.target.value })}
          />
        </label>
        <label className="cmfield">
          <span>Company{fromApi ? '' : ' *'}</span>
          <select
            value={draft.companyId ?? ''}
            required={!fromApi}
            onChange={(e) => patch({ companyId: e.target.value ? Number(e.target.value) : null })}
          >
            <option value="">{fromApi ? 'No company yet' : 'Pick a company'}</option>
            {companies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
        </label>
      </div>

      <div className="cmfields cmfields--3">
        <label className="cmfield">
          <span>Field</span>
          <input type="text" value={draft.field} readOnly={fromApi} maxLength={NAME_MAX} onChange={(e) => patch({ field: e.target.value })} />
        </label>
        <label className="cmfield">
          <span>Country</span>
          <input type="text" value={draft.country} readOnly={fromApi} maxLength={NAME_MAX} onChange={(e) => patch({ country: e.target.value })} />
        </label>
        <label className="cmfield">
          <span>Rig</span>
          <input type="text" value={draft.rig} readOnly={fromApi} maxLength={NAME_MAX} onChange={(e) => patch({ rig: e.target.value })} />
        </label>
        <label className="cmfield">
          <span>Purpose</span>
          <input
            type="text"
            value={draft.purpose}
            readOnly={fromApi}
            maxLength={NAME_MAX}
            placeholder="e.g. Exploration"
            onChange={(e) => patch({ purpose: e.target.value })}
          />
        </label>
        <label className="cmfield">
          <span>Latitude</span>
          <input
            type="text"
            inputMode="decimal"
            value={draft.latitude}
            readOnly={fromApi}
            placeholder="e.g. 5.5162"
            onChange={(e) => patch({ latitude: e.target.value })}
          />
          <small>Decimal degrees, -90 to 90</small>
        </label>
        <label className="cmfield">
          <span>Longitude</span>
          <input
            type="text"
            inputMode="decimal"
            value={draft.longitude}
            readOnly={fromApi}
            placeholder="e.g. 105.0231"
            onChange={(e) => patch({ longitude: e.target.value })}
          />
          <small>Decimal degrees, -180 to 180</small>
        </label>
      </div>

      <label className="cmfield">
        <span>Operational Status</span>
        <select value={draft.status} disabled={fromApi} onChange={(e) => patch({ status: e.target.value })}>
          {WELL_STATUSES.map((s) => (
            <option key={s} value={s}>
              {s}
            </option>
          ))}
          {!WELL_STATUSES.some((s) => s === draft.status) && <option value={draft.status}>{draft.status}</option>}
        </select>
      </label>

      <div className="cmwells" role="group" aria-label="Data sources">
        <div className="cmwells-head">
          <span>Data Sources</span>
          <button
            type="button"
            className="cmbtn cmbtn--small cmbtn--primary"
            onClick={() => patchSources((list) => [...list, { company: '', level: 1, realtime: false }])}
          >
            + Add Source
          </button>
        </div>

        {draft.sources.length === 0 && <p className="cmhint">No data sources listed yet.</p>}

        <ol className="cmsources">
          {draft.sources.map((source, index) => (
            <li key={source.id ?? `new-${index}`}>
              <span className="cmrank" aria-hidden="true">
                {index + 1}
              </span>
              <input
                type="text"
                className="cmsource-name"
                value={source.company}
                maxLength={NAME_MAX}
                placeholder="Service company"
                aria-label={`Service company of data source ${index + 1}`}
                onChange={(e) => patchSources((list) => list.map((s, i) => (i === index ? { ...s, company: e.target.value } : s)))}
              />
              <label className="cmlevel">
                Level
                <input
                  type="number"
                  min={1}
                  max={MAX_DATA_LEVEL}
                  step={1}
                  required
                  aria-label={`Level of data source ${index + 1}`}
                  value={Number.isFinite(source.level) ? source.level : ''}
                  onChange={(e) =>
                    patchSources((list) => list.map((s, i) => (i === index ? { ...s, level: e.target.valueAsNumber } : s)))
                  }
                />
              </label>
              <label className="cmcheck">
                <input
                  type="checkbox"
                  checked={source.realtime}
                  onChange={(e) =>
                    patchSources((list) => list.map((s, i) => (i === index ? { ...s, realtime: e.target.checked } : s)))
                  }
                />
                Real time
              </label>
              <button
                type="button"
                className="cmbtn cmbtn--small"
                title="More trusted"
                aria-label={`Move data source ${index + 1} up`}
                disabled={index === 0}
                onClick={() => patchSources((list) => move(list, index, -1))}
              >
                ↑
              </button>
              <button
                type="button"
                className="cmbtn cmbtn--small"
                title="Less trusted"
                aria-label={`Move data source ${index + 1} down`}
                disabled={index === draft.sources.length - 1}
                onClick={() => patchSources((list) => move(list, index, 1))}
              >
                ↓
              </button>
              <button
                type="button"
                className="cmtrash"
                aria-label={`Remove data source ${index + 1}`}
                title="Remove this source"
                onClick={() => patchSources((list) => list.filter((_, i) => i !== index))}
              >
                <svg viewBox="0 0 20 20" width="14" height="14" aria-hidden="true">
                  <path d="M7 3h6l1 2h3v2H3V5h3l1-2Zm-2 6h10l-.7 8.2a1 1 0 0 1-1 .8H6.7a1 1 0 0 1-1-.8L5 9Z" fill="currentColor" />
                </svg>
              </button>
            </li>
          ))}
        </ol>

        <p className="cmhint">
          The service companies that supply this well&apos;s data, each with its data level (1, 2, 3 or higher). The order
          is how far each is trusted, most trusted first, and it is separate from the level: a level 1 real-time feed
          can be moved to the top.
        </p>
      </div>

      {well && onDelete && <DeleteLink what="well" question={`Delete ${well.name}?`} busy={busy} onDelete={onDelete} />}
    </Dialog>
  );
}
